import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        console.log('🔍 Navigation: Fetching user permissions...');
        const response = await apiClient.get('/api/user/permissions');
        console.log('🔍 Navigation: Received permissions:', response.data);
        setUserPermissions(response.data);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
        setPermissionsLoaded(true); // Still set loaded to true to show default state
      }
    };

    if (user) {
      fetchUserPermissions();
    }
  }, [user]);

  const canAccessScheduleGeneration = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('generate_schedule');
  };

  const canAccessUserManagement = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('view_users') || userPermissions.includes('manage_beaches') || userPermissions.includes('manage_vehicles');
  };

  const canViewRuleViolations = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('view_rule_violations');
  };

  const canViewAllViolations = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('view_all_violations');
  };

  const canViewPayrollAnalytics = () => {
    if (!permissionsLoaded) {
      // Conservative: hide during loading to prevent unauthorized access
      return false;
    }
    return userPermissions.includes('view_payroll_analytics');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        <div className="nav-left">
          <Link to="/" className="nav-brand">
            {process.env.REACT_APP_BRAND_SHORT_NAME || 'LightHouse'}
          </Link>
        </div>

        <div className="nav-center">
          <ul className={`nav-links ${isMobileMenuOpen ? 'nav-links-mobile-open' : ''}`}>
          <li>
            <Link 
              to="/dashboard" 
              className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="nav-icon">🏠</span>
              <span className="nav-text">Dashboard</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/schedule-request" 
              className={`nav-link ${isActive('/schedule-request') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="nav-icon">📅</span>
              <span className="nav-text">Schedule Request</span>
            </Link>
          </li>
          {canViewRuleViolations() && (
            <li>
              <Link 
                to="/schedule-violations" 
                className={`nav-link ${isActive('/schedule-violations') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon">⚠️</span>
                <span className="nav-text">Rule Violations</span>
              </Link>
            </li>
          )}
          <li>
            <Link 
              to="/availability" 
              className={`nav-link ${isActive('/availability') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="nav-icon">👥</span>
              <span className="nav-text">Availability</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/schedule-display" 
              className={`nav-link ${isActive('/schedule-display') ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="nav-icon">📋</span>
              <span className="nav-text">Schedule Display</span>
            </Link>
          </li>
          {canAccessScheduleGeneration() && (
            <li>
              <Link 
                to="/schedule-generation" 
                className={`nav-link ${isActive('/schedule-generation') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon">⚙️</span>
                <span className="nav-text">Schedule Generation</span>
              </Link>
            </li>
          )}
          {canViewAllViolations() && (
            <li>
              <Link 
                to="/admin/schedule-violations" 
                className={`nav-link ${isActive('/admin/schedule-violations') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon">🚨</span>
                <span className="nav-text">All Violations</span>
              </Link>
            </li>
          )}
          {canViewPayrollAnalytics() && (
            <li>
              <Link 
                to="/admin/payroll-analytics" 
                className={`nav-link ${isActive('/admin/payroll-analytics') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon">📊</span>
                <span className="nav-text">Payroll Analytics</span>
              </Link>
            </li>
          )}
          {canAccessUserManagement() && (
            <li>
              <Link 
                to="/user-management" 
                className={`nav-link ${isActive('/user-management') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="nav-icon">👨‍💼</span>
                <span className="nav-text">Management</span>
              </Link>
            </li>
          )}
          </ul>
        </div>

        {/* Mobile menu button */}
        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          <span className="hamburger-icon">{isMobileMenuOpen ? '✕' : '☰'}</span>
        </button>

        <div className="nav-right">
          <div className="nav-user">
            <Link 
              to="/profile" 
              className={`nav-user-name ${isActive('/profile') ? 'active' : ''}`}
              title="Go to Profile"
            >
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user?.username || 'User'}
            </Link>
            <button onClick={logout} className="logout-btn">
              <span className="logout-icon">🚪</span>
              <span className="logout-text">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 